import * as React from 'react';
import { ArrowDownIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

type ScrollPillProps = React.HTMLAttributes<HTMLDivElement> & {
  pillLabel?: string;
};

// How far the content gently fades out at a scrollable edge. Subtle on purpose.
const EDGE_FADE = '2rem';

/**
 * A scroll container with two scroll affordances:
 *   1. Subtle edge fades — the content fades out at the top (once you've
 *      scrolled down) and at the bottom (while there's more below), so it's
 *      clear the area scrolls. A `mask-image` fades the real content into the
 *      canvas behind it; each edge only fades when it can actually scroll, so
 *      nothing is clipped when there's nowhere to go.
 *   2. A floating "scroll for more" pill while there's more content below.
 */
export function ScrollPill({
  children,
  className,
  pillLabel = 'Scroll for more',
  ...props
}: ScrollPillProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [showPill, setShowPill] = React.useState(false);
  const [edges, setEdges] = React.useState({ top: false, bottom: false });

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight - el.clientHeight > 1;
    const distanceFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    setShowPill(hasOverflow && distanceFromBottom > 24);
    const top = el.scrollTop > 4;
    const bottom = hasOverflow && distanceFromBottom > 4;
    // Only re-render when an edge actually flips (cheap on every scroll frame).
    setEdges(prev =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
    );
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    Array.from(el.children).forEach(child => resizeObserver.observe(child));

    const mutationObserver = new MutationObserver(updateScrollState);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [updateScrollState]);

  const handleAdvance = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
  };

  // Fade only the edges that can actually scroll, so content is never clipped
  // when there's nothing more in that direction. The content dims to ~75% at
  // the very edge (never fully vanishing) and eases up to fully opaque inward,
  // so the fade reads as a soft, natural dissolve.
  const topStops = edges.top
    ? `rgba(0,0,0,0.75) 0, rgba(0,0,0,0.85) calc(${EDGE_FADE} * 0.5), rgba(0,0,0,0.93) calc(${EDGE_FADE} * 0.78), #000 ${EDGE_FADE}`
    : '#000 0';
  const bottomStops = edges.bottom
    ? `#000 calc(100% - ${EDGE_FADE}), rgba(0,0,0,0.93) calc(100% - ${EDGE_FADE} * 0.78), rgba(0,0,0,0.85) calc(100% - ${EDGE_FADE} * 0.5), rgba(0,0,0,0.75) 100%`
    : '#000 100%';
  const maskImage = `linear-gradient(to bottom, ${topStops}, ${bottomStops})`;

  return (
    <div
      className={cn('relative isolate flex flex-col min-h-0', className)}
      {...props}
    >
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto min-h-0"
        style={{ maskImage, WebkitMaskImage: maskImage }}
      >
        {children}
      </div>

      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-8 z-20 flex justify-center transition-opacity duration-300',
          showPill ? 'opacity-100' : 'opacity-0'
        )}
      >
        <button
          type="button"
          onClick={handleAdvance}
          aria-label={pillLabel}
          tabIndex={showPill ? 0 : -1}
          className={cn(
            // Spec: 30px tall, padding 7px 11px, gap 4px, bg --secondary, 1px border, full radius
            'pointer-events-auto inline-flex items-center gap-1 h-[30px] px-[11px] py-[7px] rounded-full border border-border bg-secondary text-xs font-medium text-foreground shadow-elev-2',
            'hover:bg-button-hovered transition-colors',
            'animate-scroll-pill-bob'
          )}
        >
          <ArrowDownIcon className="size-3.5" weight="bold" />
          {pillLabel}
        </button>
      </div>
    </div>
  );
}
